import { useEffect, useRef } from 'react'
import { speichereDatei, dateiname } from '../net/speichern.js'
import { api } from '../net/apiClient.js'

/**
 * Das PDF, ohne die App zu verlassen.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum es diese Ansicht gibt
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Bisher stand hinter «PDF ansehen» ein Link mit `target="_blank"`. Auf einem
 * Rechner ist das richtig: ein neuer Reiter, und der alte bleibt stehen.
 *
 * Auf dem Telefon ist es eine Sackgasse. Der Browser öffnet das PDF in seiner
 * eigenen Anzeige, und die hat keinen Weg zurück in die Anwendung – wer die
 * Rechnung angeschaut hat, muss die App schliessen und von vorn anfangen.
 * Als Web-App vom Startbildschirm ist es noch schlimmer: dort führt der Link
 * aus der App heraus in den Browser, und der Rückweg ist der Home-Knopf.
 *
 * Deshalb bleibt das PDF hier drin: eine Ansicht über der Seite, mit einem
 * Zurück-Knopf oben links, wo ihn jede Hand erwartet. Darunter zeigt ein
 * Rahmen die Datei. Was der Rahmen nicht kann – manche Telefone zeigen darin
 * nur die erste Seite –, fangen die beiden Knöpfe daneben ab: im Browser
 * öffnen oder herunterladen. Beides ist dann eine Entscheidung und keine
 * Falle.
 *
 * Die Escape-Taste schliesst ebenfalls, und solange die Ansicht offen ist,
 * scrollt die Seite darunter nicht mit.
 */
export function PdfAnsicht({
  url,
  titel,
  aufZu
}: {
  url: string
  titel: string
  aufZu: () => void
}): JSX.Element {
  const zurueck = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    /*
      Der Blick landet auf dem Zurück-Knopf.

      Wer mit der Tastatur arbeitet, steht sonst irgendwo im Rahmen und
      kommt nur mit der Maus wieder heraus.
    */
    zurueck.current?.focus()

    const taste = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') aufZu()
    }
    document.addEventListener('keydown', taste)

    // Die Seite darunter soll nicht mitscrollen, sonst verliert man die Stelle.
    const vorher = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', taste)
      document.body.style.overflow = vorher
    }
  }, [aufZu])

  return (
    <div className="pdf-ansicht" role="dialog" aria-modal="true" aria-label={titel}>
      <div className="pdf-ansicht-kopf">
        <button type="button" className="pdf-zurueck" ref={zurueck} onClick={aufZu} id="btn-pdf-zurueck">
          <span aria-hidden="true">←</span> Zurück
        </button>
        <span className="pdf-ansicht-titel">{titel}</span>
        <span className="pdf-ansicht-griffe">
          <a className="pdf-griff" href={url} target="_blank" rel="noreferrer" id="btn-pdf-extern">
            Im Browser
          </a>
          {/*
            «Herunterladen» war ein Verweis mit `download`. Der legt die
            Datei dorthin, wo der Browser immer hinlegt – und genau das war
            die Meldung: Der Betrieb möchte den Ordner selbst wählen.

            `speichereDatei` öffnet den nativen «Speichern unter …» Dialog,
            wo der Browser ihn hat (Chrome, Edge auf dem Schreibtisch), und
            nimmt sonst denselben Weg wie vorher. Auf dem Telefon ist das
            der Speichern- und Teilen-Dialog des Systems – eine Wahl, nur
            vom Browser geführt.
          */}
          <button
            type="button"
            className="pdf-griff"
            id="btn-pdf-laden"
            onClick={() => {
              void speichereDatei(url, dateiname([titel], 'pdf'), () => api.dateiLaden(url))
            }}
          >
            Herunterladen
          </button>
        </span>
      </div>
      <div className="pdf-ansicht-blatt">
        <iframe className="pdf-rahmen" src={url} title={titel} />
      </div>
    </div>
  )
}
